import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/**
 * CreatorTour
 * A creator-facing spotlight tour that auto-boots once, the first time
 * a user with a minted Clan lands on `/manage-clan`. Highlights the
 * creator dashboard essentials (earnings, params, view public) and
 * explains the Perks / Bounties / Growth features that live one click
 * away on the public Clan page.
 *
 * Persistence: `POST /api/auth/creator-tour-complete` sets
 * `user.creator_tour_completed_at`. Manual restart via
 * `POST /api/auth/creator-tour-reset` from the Help Center.
 *
 * Copy lives in i18n under `tours.creator.*` — STEPS holds only ids,
 * selectors, and placement so translations override per-locale.
 *
 * Kept as a separate component (not merged into OnboardingTour) so the
 * two tours run independently — a user can complete the fan tour and
 * still get the creator tour once they mint.
 */

const STEPS = [
  { id: 'welcome',    target: null                                                              },
  { id: 'clanHq',     target: '[data-testid="mc-clan-info"]',           placement: 'bottom'     },
  { id: 'earnings',   target: '[data-testid="mc-earnings-breakdown"]',  placement: 'bottom'     },
  { id: 'params',     target: '[data-testid="mc-clan-params"]',         placement: 'top'        },
  { id: 'perks',      target: null                                                              },
  { id: 'bounties',   target: null                                                              },
  { id: 'growth',     target: null                                                              },
  { id: 'viewPublic', target: '[data-testid="mc-view-public"]',         placement: 'left'       },
  { id: 'done',       target: null,                                     hasDoneCta: true        },
];

const PADDING = 8;

export default function CreatorTour() {
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const bootedRef = useRef(false);
  const [myClanHandle, setMyClanHandle] = useState(null);

  // Auto-boot once, the first time a creator with a minted Clan lands
  // on /manage-clan. Gates:
  //  - `user.creator_tour_completed_at` unset (fresh)
  //  - user has a minted Clan (checked via /api/clans/mine)
  //  - not an admin, not suspended
  useEffect(() => {
    if (bootedRef.current) return;
    if (!user || user === false) return;
    if (user.creator_tour_completed_at) return;
    if (user.role === 'admin') return;
    if (user.suspended) return;
    if (location.pathname !== '/manage-clan') return;
    bootedRef.current = true;
    // Fetch the user's Clan so the final "View my public Clan" CTA has
    // a real handle to deep-link to. If they don't have one, don't boot.
    (async () => {
      try {
        const { data } = await api.get('/clans/mine');
        if (!data?.clan?.handle) return;
        setMyClanHandle(data.clan.handle);
        // Give ManageClan a beat to render its anchors before we measure.
        setTimeout(() => {
          setActive(true);
          setIdx(0);
        }, 900);
      } catch (_) { /* no clan → no tour */ }
    })();
  }, [user, location.pathname]);

  const step = active ? STEPS[idx] : null;

  useLayoutEffect(() => {
    if (!active || !step) { setRect(null); setReady(false); return; }
    if (!step.target) { setRect(null); setReady(true); return; }

    let cancelled = false;
    let tries = 0;
    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        // Scroll the target into view so the spotlight actually lands
        // on-screen even when the section is below the fold.
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // Small delay lets the smooth scroll settle before we measure.
        setTimeout(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          setReady(true);
        }, 250);
        return;
      }
      if (++tries < 20) {
        setTimeout(measure, 100);
      } else {
        setReady(true);
        setRect(null);
      }
    };
    measure();

    const onScroll = () => {
      if (!step.target) return;
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [active, idx, step]);

  const finish = useCallback(async (goPublic = false) => {
    setActive(false);
    try {
      await api.post('/auth/creator-tour-complete');
      await refreshMe();
    } catch (_) { /* non-fatal */ }
    if (goPublic && myClanHandle) {
      navigate(`/clan/${myClanHandle}/feed`, { state: { from: '/tour' } });
    }
  }, [refreshMe, navigate, myClanHandle]);

  const next = useCallback(() => {
    if (idx >= STEPS.length - 1) { finish(true); return; }
    setIdx((i) => i + 1);
  }, [idx, finish]);

  const back = useCallback(() => {
    setIdx((i) => Math.max(0, i - 1));
  }, []);

  if (!active || !step || !ready) return null;

  const isCentered = !step.target || !rect;
  const isFinal = idx >= STEPS.length - 1;

  const tooltipStyle = (() => {
    if (isCentered) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const vw = window.innerWidth;
    const tooltipW = Math.min(340, vw - 24);
    const placement = step.placement || 'bottom';
    let top = rect.top + rect.height + 12;
    let left = rect.left + rect.width / 2 - tooltipW / 2;
    if (placement === 'left') {
      top = rect.top + rect.height / 2 - 60;
      left = rect.left - tooltipW - 12;
    } else if (placement === 'top') {
      top = rect.top - 160;
      left = rect.left + rect.width / 2 - tooltipW / 2;
    } else if (placement === 'bottom-end') {
      top = rect.top + rect.height + 12;
      left = rect.left + rect.width - tooltipW;
    }
    left = Math.max(12, Math.min(left, vw - tooltipW - 12));
    top = Math.max(12, top);
    return { top: `${top}px`, left: `${left}px`, width: `${tooltipW}px` };
  })();

  return (
    <div className="tour-root" data-testid="creator-tour-root" role="dialog" aria-modal="true">
      {isCentered ? (
        <div className="tour-backdrop tour-backdrop-solid" onClick={() => next()} />
      ) : (
        <>
          <div className="tour-backdrop-panel" style={{ top: 0, left: 0, right: 0, height: rect.top - PADDING }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top - PADDING, left: 0, width: rect.left - PADDING, height: rect.height + PADDING * 2 }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top - PADDING, left: rect.left + rect.width + PADDING, right: 0, height: rect.height + PADDING * 2 }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top + rect.height + PADDING, left: 0, right: 0, bottom: 0 }} />
          <div
            className="tour-spotlight"
            style={{
              top: rect.top - PADDING,
              left: rect.left - PADDING,
              width: rect.width + PADDING * 2,
              height: rect.height + PADDING * 2,
            }}
          />
        </>
      )}

      <div className="tour-tooltip" style={tooltipStyle} data-testid={`creator-tour-step-${step.id}`}>
        <div className="tour-step-count">{t('tours.creator.stepCount', { current: idx + 1, total: STEPS.length })}</div>
        <div className="tour-title">{t(`tours.creator.${step.id}Title`)}</div>
        <div className="tour-body">{t(`tours.creator.${step.id}Body`)}</div>
        <div className="tour-actions">
          <button className="tour-btn-ghost" onClick={() => finish(false)} data-testid="creator-tour-skip-btn">
            {t('tours.common.skipTour')}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {idx > 0 && (
              <button className="tour-btn-ghost" onClick={back} data-testid="creator-tour-back-btn">
                {t('tours.common.back')}
              </button>
            )}
            <button className="tour-btn-primary" onClick={next} data-testid="creator-tour-next-btn">
              {isFinal ? (step.hasDoneCta ? t('tours.creator.doneCta') : t('tours.common.done')) : t('tours.common.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
